import type { Session } from '@iq/contracts/v1/auth';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { api } from './api';

export type SessionState =
  | { status: 'loading' }
  | { status: 'authenticated'; session: Session }
  | { status: 'unauthenticated' };

type SessionQueryResult = { authenticated: true; session: Session } | { authenticated: false };

const sessionQuery = {
  queryKey: ['auth', 'session'] as const,
  queryFn: async (): Promise<SessionQueryResult> => {
    const { data, error, response } = await api.GET('/auth/session');
    if (error) {
      if (response.status === 401) return { authenticated: false } as const;
      throw error;
    }
    if (!data) {
      // Defensive: openapi-fetch returns either `data` or `error`, never both
      // empty. If we ever land here it's a server contract bug — surface it.
      throw new Error('Empty 200 body from /auth/session');
    }
    return { authenticated: true, session: data } as const;
  },
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: true,
};

/**
 * React hook returning the current session as a discriminated union so call
 * sites can branch without checking `undefined`.
 */
export function useSession(): SessionState {
  const query = useQuery(sessionQuery);

  if (query.isPending) return { status: 'loading' };
  if (query.data?.authenticated === true) {
    return { status: 'authenticated', session: query.data.session };
  }
  return { status: 'unauthenticated' };
}

/**
 * Router `beforeLoad` helper for PR3. Exported now so PR2 tests cover it.
 * Uses `fetchQuery` so the result is cached and re-used by `useSession`.
 */
export async function ensureSession(client: QueryClient): Promise<SessionState> {
  const result = await client.fetchQuery(sessionQuery);
  if (result.authenticated) {
    return { status: 'authenticated', session: result.session };
  }
  return { status: 'unauthenticated' };
}
